import type { Auth, User } from "firebase/auth";
import { signOut } from "./signOut";
import { type FormEvent, useEffect, useRef, useState } from "react";
import * as api from "./api";
import { callDay, callTime, emailName } from "./calls";
import { FOLLOW_UP_COLUMNS } from "./columns";
import { byOwner, dueText, type FollowUpDue, type FollowUpView, followUpError, isLate, MISSING_TEXT } from "./followUps";
import { go, useTitle } from "./hooks";
import { STAGE_TEXT } from "./leads";
import { todayIn } from "./pickup";
import { type Saved, SERVER_PROBLEM, saveFailure } from "./save";
import { type Sort, sortRows } from "./sort";
import { SortSelect, SortTh, useSort } from "./SortHeader";
import { memberName } from "./memberNames";
import { Skeleton } from "./Skeleton";

const DONE_SHOWN = 5;

function SessionButton({ saved, auth }: { saved: Saved | null; auth: Auth }) {
  if (!saved || saved.ok || !saved.session) return null;
  return (
    <>
      {" "}
      <button type="button" onClick={() => signOut(auth)}>
        Sign in again
      </button>
    </>
  );
}

function MissingChips({ item }: { item: api.FollowUp }) {
  if (!item.missing.length) return <>–</>;
  return (
    <>
      {item.missing.map((key) => (
        <span key={key} className="chip-flag" data-testid="follow-up-missing">
          {MISSING_TEXT[key]}
        </span>
      ))}
    </>
  );
}

function useFinish(user: User, onDone: (item: api.FollowUp) => void) {
  const [busy, setBusy] = useState<string | null>(null);
  const [saved, setSaved] = useState<Saved | null>(null);
  const finish = async (item: api.FollowUp) => {
    setBusy(item.id);
    setSaved(null);
    try {
      const next = await api.finishFollowUp(await user.getIdToken(), item.id);
      onDone(next.follow_up);
      setSaved({ ok: true, text: `Done: ${item.lead_name}.` });
    } catch (e) {
      setSaved(saveFailure(e, "The change"));
    } finally {
      setBusy(null);
    }
  };
  return { busy, saved, finish };
}

/** Done is permanent, so the button asks first (rule 16.2). */
function DoneButton({ item, busy, testId, onFinish }: { item: api.FollowUp; busy: string | null; testId: string; onFinish: (item: api.FollowUp) => void }) {
  const [asking, setAsking] = useState(false);
  if (busy === item.id) return <button type="button" disabled>Saving…</button>;
  if (!asking) {
    return (
      <button type="button" data-testid={testId} onClick={() => setAsking(true)}>
        Done
      </button>
    );
  }
  return (
    <span role="group" aria-label={`Mark the follow-up for ${item.lead_name} done`}>
      <button
        type="button"
        className="primary"
        data-testid={`${testId}-confirm`}
        autoFocus
        onClick={() => {
          setAsking(false);
          onFinish(item);
        }}
      >
        Mark done
      </button>{" "}
      <button type="button" data-testid={`${testId}-keep`} onClick={() => setAsking(false)}>
        Keep open
      </button>
    </span>
  );
}

function FollowUpTable({
  items,
  today,
  base,
  label,
  busy,
  onFinish,
  sort,
  onSort,
}: {
  items: api.FollowUp[];
  today: string;
  base: string;
  label: string;
  busy: string | null;
  onFinish: (item: api.FollowUp) => void;
  sort: Sort | null;
  onSort: (next: Sort | null) => void;
}) {
  return (
    <div className="table-scroll" tabIndex={0} role="region" aria-label={label}>
      <table className="list follow-ups-table" data-testid="follow-up-table" data-cols="follow-ups-select">
        <thead>
          <tr>
            {FOLLOW_UP_COLUMNS.map((column) => (
              <SortTh key={column.key} column={column} sort={sort} onSort={onSort} />
            ))}
            <th scope="col">
              <span className="visually-hidden">Done</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {sortRows(items, FOLLOW_UP_COLUMNS, sort).map((item) => (
            <tr key={item.id} data-testid="follow-up-row" data-late={isLate(item.due_on, today) || undefined}>
              <th scope="row">
                <a href={`${base}/leads/${item.lead_id}`} onClick={go} data-testid="follow-up-lead">
                  {item.lead_name}
                </a>
              </th>
              <td data-label="Due" className={isLate(item.due_on, today) ? "late" : undefined} title={item.due_on}>
                {dueText(item.due_on, item.due_at, today)}
              </td>
              <td data-label="To do" title={item.note}>
                <span className="clip">{item.note}</span>
              </td>
              <td data-label="Missing data">
                <MissingChips item={item} />
              </td>
              <td data-label="Stage">{STAGE_TEXT[item.stage]}</td>
              <td className="sticky-end">
                {item.can_edit && <DoneButton item={item} busy={busy} testId="follow-up-done" onFinish={onFinish} />}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

type ListLoad = api.FollowUpList | "server" | "network" | "session" | "hidden" | undefined;

function readChoice(): { view: FollowUpView; due: FollowUpDue } {
  const params = new URLSearchParams(window.location.search);
  return { view: params.get("view") === "all" ? "all" : "mine", due: params.get("due") === "open" ? "open" : "now" };
}

function writeChoice(view: FollowUpView, due: FollowUpDue) {
  const url = new URL(window.location.href);
  url.searchParams.set("view", view);
  url.searchParams.set("due", due);
  window.history.replaceState(window.history.state, "", url);
}

/** The follow-up queue (F-SAL-6): the follow-ups that are due, by salesperson, with the missing data. */
export function FollowUpList({ base, org, auth, user, canView }: { base: string; org: api.Bootstrap; auth: Auth; user: User; canView: boolean | undefined }) {
  const [view, setView] = useState<FollowUpView>(() => readChoice().view);
  const [due, setDue] = useState<FollowUpDue>(() => readChoice().due);
  const [found, setFound] = useState<ListLoad>(undefined);
  const [attempt, setAttempt] = useState(0);
  const [sort, setSort] = useSort("sort", FOLLOW_UP_COLUMNS);
  const { busy, saved, finish } = useFinish(user, (done) =>
    setFound((before) => (typeof before === "object" ? { ...before, follow_ups: before.follow_ups.filter((item) => item.id !== done.id) } : before)),
  );
  useTitle(`Follow-ups · ${org.name}`);
  useEffect(() => {
    if (canView !== true) return;
    let live = true;
    user
      .getIdToken()
      .then((token) => api.followUps(token, view, due))
      .then((list) => {
        if (!live) return;
        setFound(list);
        if (view === "mine" && !list.can_own) setView("all");
      })
      .catch((e) => {
        const code = e instanceof api.ApiError ? e.status : 0;
        if (live) setFound(code === 404 ? "hidden" : code === 401 ? "session" : code >= 500 ? "server" : "network");
      });
    return () => {
      live = false;
    };
  }, [user, view, due, attempt, canView]);
  const pick = (nextView: FollowUpView, nextDue: FollowUpDue) => {
    setView(nextView);
    setDue(nextDue);
    writeChoice(nextView, nextDue);
  };
  if (canView === false || found === "hidden") {
    return (
      <main data-testid="follow-ups-hidden">
        <h1 tabIndex={-1}>Follow-ups</h1>
        <p>Your role does not show follow-ups.</p>
      </main>
    );
  }
  const list = typeof found === "object" ? found : null;
  const today = list?.today ?? todayIn(org.time_zone);
  const groups = list ? byOwner(list.follow_ups) : [];
  const tabs: [FollowUpView, string][] = [...(list?.can_own === false ? [] : [["mine", "Mine"] as [FollowUpView, string]]), ["all", "All that I can see"]];
  return (
    <main className="follow-ups-page wide" data-testid="follow-ups-page">
      <h1 tabIndex={-1}>Follow-ups</h1>
      <div className="tabs" role="group" aria-label="Whose follow-ups">
        {tabs.map(([value, label]) => (
          <button key={value} type="button" aria-pressed={view === value} data-testid={`follow-ups-${value}`} onClick={() => pick(value, due)}>
            {label}
          </button>
        ))}
      </div>
      <div className="tabs stages" role="group" aria-label="Which follow-ups">
        <button type="button" aria-pressed={due === "now"} data-testid="follow-ups-now" onClick={() => pick(view, "now")}>
          Due today or late
        </button>
        <button type="button" aria-pressed={due === "open"} data-testid="follow-ups-open" onClick={() => pick(view, "open")}>
          All open
        </button>
      </div>
      <p role="status" data-testid="follow-up-status" className={saved && !saved.ok ? "error" : undefined}>
        {saved?.text}
        <SessionButton saved={saved} auth={auth} />
      </p>
      {found === undefined ? (
        <Skeleton shape="table" columns={5} />
      ) : found === "session" ? (
        <p role="alert" className="error">
          Your session ended.{" "}
          <button type="button" onClick={() => signOut(auth)}>
            Sign in again
          </button>
        </p>
      ) : typeof found === "string" ? (
        <p role="alert" className="error">
          {found === "server" ? SERVER_PROBLEM : "Cannot reach the server. Check your connection."}{" "}
          <button type="button" onClick={() => setAttempt((n) => n + 1)}>
            Try again
          </button>
        </p>
      ) : groups.length === 0 ? (
        <p data-testid="follow-ups-none">{due === "now" ? "No follow-ups are due today or late." : "No open follow-ups."}</p>
      ) : (
        <>
          <SortSelect id="follow-ups-select" columns={FOLLOW_UP_COLUMNS} sort={sort} onSort={setSort} fallback="Due first" />
          {groups.map(([owner, items]) => {
            const title = owner === null ? "No owner (the inbox)" : view === "mine" ? "Your follow-ups" : owner;
            return (
              <section key={owner ?? "inbox"} aria-label={title} data-testid="follow-up-group">
                <h2 title={owner ?? undefined}>
                  {owner !== null && view !== "mine" ? emailName(owner) : title} ({items.length})
                </h2>
                <FollowUpTable items={items} today={today} base={base} label={title} busy={busy} onFinish={(item) => void finish(item)} sort={sort} onSort={setSort} />
              </section>
            );
          })}
        </>
      )}
    </main>
  );
}

/** The follow-ups of one lead, with the form that plans the next one (F-SAL-5). */
export function LeadFollowUps({
  lead,
  items,
  org,
  auth,
  user,
  onSaved,
}: {
  lead: api.Lead;
  items: api.FollowUp[];
  org: api.Bootstrap;
  auth: Auth;
  user: User;
  onSaved: (next: api.LeadDetail) => void;
}) {
  const today = todayIn(org.time_zone);
  const [shown, setShown] = useState(items);
  useEffect(() => setShown(items), [items]);
  const { busy, saved: doneSaved, finish } = useFinish(user, (done) => setShown((before) => before.map((item) => (item.id === done.id ? done : item))));
  const [dueOn, setDueOn] = useState(today);
  const [dueAt, setDueAt] = useState("");
  const [note, setNote] = useState("");
  const [saved, setSaved] = useState<Saved | null>(null);
  const [saving, setSaving] = useState(false);
  const entryId = useRef<string | null>(null);
  const edit = (next: () => void) => {
    next();
    entryId.current = null;
    setSaved(null);
  };
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    const problem = followUpError(dueOn, note, today);
    if (problem) {
      setSaved({ ok: false, field: true, text: problem });
      return;
    }
    entryId.current ??= crypto.randomUUID();
    setSaving(true);
    setSaved(null);
    try {
      const entry: api.FollowUpEntry = { id: entryId.current, due_on: dueOn, due_at: dueAt || null, note: note.trim() };
      const next = await api.addFollowUp(await user.getIdToken(), lead.id, entry);
      entryId.current = null;
      setNote("");
      setDueAt("");
      onSaved(next);
      setSaved({ ok: true, text: `Planned for ${dueText(entry.due_on, entry.due_at, today).toLowerCase()}.` });
    } catch (e) {
      setSaved(saveFailure(e, "The follow-up"));
    } finally {
      setSaving(false);
    }
  };
  const open = shown.filter((item) => !item.done_at);
  const done = shown.filter((item) => item.done_at).slice(-DONE_SHOWN);
  const fieldError = saved && !saved.ok && saved.field ? saved.text : null;
  return (
    <section aria-labelledby="lead-follow-ups" data-testid="lead-follow-ups">
      <h2 id="lead-follow-ups">Follow-ups</h2>
      {open.length ? (
        <ul className="calls">
          {open.map((item) => (
            <li key={item.id} data-testid="lead-follow-up" data-due={item.due_on}>
              <p>
                <strong className={isLate(item.due_on, today) ? "late" : undefined}>{dueText(item.due_on, item.due_at, today)}</strong> · {item.note}{" "}
                {item.can_edit && <DoneButton item={item} busy={busy} testId="lead-follow-up-done" onFinish={(next) => void finish(next)} />}
              </p>
            </li>
          ))}
        </ul>
      ) : (
        <p>No open follow-ups.</p>
      )}
      {done.length > 0 && (
        <ul className="calls done">
          {done.map((item) => (
            <li key={item.id} className="sub" data-testid="lead-follow-up-closed" data-day={item.done_at ? callDay(item.done_at, org.time_zone) : undefined}>
              Done {item.done_at ? callTime(item.done_at, org.time_zone) : ""} by {item.done_email ? memberName(item.done_email) : "a removed member"} · {item.note}
            </li>
          ))}
        </ul>
      )}
      <p role="status" data-testid="lead-follow-up-done-status" className={doneSaved && !doneSaved.ok ? "error" : undefined}>
        {doneSaved?.text}
        <SessionButton saved={doneSaved} auth={auth} />
      </p>
      {lead.can_edit && (
        <form onSubmit={submit} noValidate className="lead-form" data-testid="follow-up-form" aria-labelledby="follow-up-title">
          <h3 id="follow-up-title">Plan a follow-up</h3>
          <label htmlFor="follow-up-day">Day</label>
          <input id="follow-up-day" data-testid="follow-up-day" type="date" min={today} value={dueOn} onChange={(e) => edit(() => setDueOn(e.target.value))} />
          <label htmlFor="follow-up-time">Time (optional)</label>
          <input id="follow-up-time" data-testid="follow-up-time" type="time" value={dueAt} onChange={(e) => edit(() => setDueAt(e.target.value))} />
          <label htmlFor="follow-up-note">What to do</label>
          <textarea
            id="follow-up-note"
            data-testid="follow-up-note"
            rows={2}
            value={note}
            onChange={(e) => edit(() => setNote(e.target.value))}
            aria-invalid={fieldError ? true : undefined}
            aria-describedby={fieldError ? "follow-up-error" : undefined}
          />
          {fieldError && (
            <p id="follow-up-error" role="alert" className="error" data-testid="follow-up-error">
              {fieldError}
            </p>
          )}
          <div className="actions">
            <button data-testid="save-follow-up" disabled={saving}>
              {saving ? "Saving…" : "Plan the follow-up"}
            </button>
          </div>
          <p role="status" data-testid="follow-up-form-status" className={saved && !saved.ok && !saved.field ? "error" : undefined}>
            {saved && !fieldError && saved.text}
            <SessionButton saved={saved} auth={auth} />
          </p>
        </form>
      )}
    </section>
  );
}
